/**
 * ComparisonDashboard (FEAT-ANALYTICS-002) — GA "Add comparison" overlay. Up to
 * 4 audiences (incl. "All users") compared side-by-side on core metrics, with an
 * overlaid users-over-time chart. Fetches the uniform /analytics/audience-metrics
 * endpoint once per audience, so it works regardless of the active report.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer, HStack,
} from '@chakra-ui/react';
import { fetchReport } from '../api';
import { Panel, TrendChart, ReportFrame, fmtInt, fmtDuration } from '../components';
import { fmtMoney } from '../txnFormat';

const METRICS = [
  { key: 'total_users', label: 'Users', fmt: fmtInt },
  { key: 'new_users', label: 'New users', fmt: fmtInt },
  { key: 'sessions', label: 'Sessions', fmt: fmtInt },
  { key: 'views_per_session', label: 'Views / session', fmt: (v) => Number(v || 0).toFixed(2) },
  { key: 'avg_session_seconds', label: 'Avg. session', fmt: fmtDuration },
  { key: 'revenue', label: 'Revenue', fmt: (v) => fmtMoney(v) },
];

const catOf = (b = []) => b.map((x) => String(x || '').replace(/ 00:00:00$/, '').slice(5));

export function ComparisonDashboard({ comparisons = [], params = {} }) {
  const [state, setState] = useState({ loading: true, error: null, results: [] });
  const key = JSON.stringify({ c: comparisons.map((x) => x.key), params });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.all(
      comparisons.map((c) =>
        fetchReport('audience-metrics', { ...params, ...(c.definition ? { audience: JSON.stringify(c.definition) } : {}) })
          .then((data) => ({ comp: c, data }))
          .catch(() => ({ comp: c, data: null }))
      )
    ).then((results) => { if (alive) setState({ loading: false, error: null, results }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Align each audience's daily-users series onto one bucket axis.
  const chart = useMemo(() => {
    const buckets = [...new Set(state.results.flatMap((r) => r.data?.series?.buckets || []))]
      .sort((a, b) => String(a).localeCompare(String(b)));
    const series = state.results.map((r) => {
      const s = r.data?.series || { buckets: [], users: [] };
      const m = {};
      s.buckets.forEach((b, i) => { m[b] = s.users[i]; });
      return { name: r.comp.name, data: buckets.map((b) => Number(m[b] || 0)), color: r.comp.color };
    });
    return { categories: catOf(buckets), series };
  }, [state.results]);

  return (
    <ReportFrame loading={state.loading} error={state.error}>
      {!state.loading && (
        <Box>
          <Panel title="Users over time — audience comparison">
            <TrendChart categories={chart.categories} series={chart.series} />
          </Panel>

          <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="14px" boxShadow="0 1px 2px rgba(16,24,40,.04)" mt={4}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.500">Audience</Th>
                    {METRICS.map((m) => <Th key={m.key} isNumeric color="gray.500">{m.label}</Th>)}
                  </Tr>
                </Thead>
                <Tbody>
                  {state.results.map((r) => (
                    <Tr key={r.comp.key} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <HStack><Box w="10px" h="10px" borderRadius="full" bg={r.comp.color} /><Text fontWeight="600">{r.comp.name}</Text></HStack>
                      </Td>
                      {METRICS.map((m) => (
                        <Td key={m.key} isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.data ? m.fmt(r.data.metrics?.[m.key]) : '—'}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          <Text fontSize="xs" color="gray.400" mt={2}>Comparing {comparisons.length} audiences on core metrics over the selected window. Add or remove comparisons in the toolbar.</Text>
        </Box>
      )}
    </ReportFrame>
  );
}

export default ComparisonDashboard;
